import { useMemo, useState } from "react";
import { mangaList, categoryMeta, type Manga } from "@/mocks/manga";
import MangaCard from "@/components/base/MangaCard";
import Seo from "@/components/base/Seo";

export default function CategoryList({ category }: { category: Manga["category"] }) {
  const meta = categoryMeta[category];
  const items = mangaList.filter((m) => m.category === category);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `番木瓜漫画 - ${meta.name}`,
      description: meta.desc,
    },
  ];

  const allTags = useMemo(
    () => Array.from(new Set(items.flatMap((m) => m.tags))),
    [items]
  );
  const [activeTag, setActiveTag] = useState("全部");

  const filtered =
    activeTag === "全部"
      ? items
      : items.filter((m) => m.tags.includes(activeTag));

  return (
    <>
      <Seo
        title={`番木瓜漫画 - ${meta.name}`}
        description={meta.desc}
        keywords={`番木瓜漫画,${meta.name},番木瓜漫画${meta.name}`}
        jsonLd={jsonLd}
      />
      <div className="pt-16">
        {/* page header */}
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <nav aria-label="面包屑" className="text-xs text-background-200/70">
              <a href="/" className="transition hover:text-primary-300">
                首页
              </a>
              <span className="mx-2">/</span>
              <span className="text-primary-300">{meta.name}</span>
            </nav>
            <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-4xl">
              {meta.name}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-background-100/85 md:text-base">
              {meta.desc}
            </p>
            <div className="mt-5 flex items-center gap-4 text-sm text-background-100/80">
              <span className="flex items-center gap-1.5">
                <i className="ri-book-3-line text-primary-300"></i>
                收录 {items.length} 部作品
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-primary-300"></i>
                每日更新
              </span>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
            {/* tag filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 flex items-center gap-1 text-sm font-bold text-foreground-900">
                <i className="ri-filter-3-line text-primary-600"></i>
                题材筛选
              </span>
              <button
                onClick={() => setActiveTag("全部")}
                className={`rounded-full px-4 py-1.5 text-sm transition whitespace-nowrap ${
                  activeTag === "全部"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-100 text-foreground-700 hover:bg-primary-100"
                }`}
              >
                全部
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTag(t)}
                  className={`rounded-full px-4 py-1.5 text-sm transition whitespace-nowrap ${
                    activeTag === t
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-100 text-foreground-700 hover:bg-primary-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <p className="mt-4 text-sm text-foreground-500">
              当前显示：<span className="font-bold text-primary-700">{activeTag}</span> 题材，
              共 {filtered.length} 部作品
            </p>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
              {filtered.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-background-300 py-16 text-center">
                <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-500">
                  该题材下暂时没有作品，看看其他分类吧
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}